/**
 * @module composables/useEpisodePlayer
 *
 * ── FLOW / PURPOSE ─────────────────────────────────────────────────────
 *
 * Singleton state for the standalone "Play Episode" quick-play feature:
 * play a single episode immediately -- caching first if needed, seamlessly --
 * WITHOUT adding it to the playlist queue. This is deliberately independent
 * of useMediaPlayer.js's playlist/queue state: it does NOT touch
 * episodes/currentEpisodeIndex/showPlayer/openPlayer/closePlayer/
 * queueAndPlay/playAllFromIndex/finishAndAdvance/the sleep timer. Reusing
 * that queue for a single "not really in the playlist" episode would trigger
 * finishAndAdvance()'s autoRemovePlayed behavior on natural episode end,
 * which deletes a row from the user's REAL, persisted playlist -- exactly
 * the bug this composable exists to avoid.
 *
 * What IS safely reused from useMediaPlayer.js: only the ID-keyed,
 * non-queue-coupled position helpers (getEpisodePositionWithIntroSkip /
 * setEpisodePosition / resetEpisodePosition / saveToStorage), so resume
 * position stays consistent whether an episode was last played via the
 * playlist or via quick-play.
 *
 * Closing this player does NOT stop playback: EpisodePlayerDialog.vue hands
 * the video off to the browser's native Picture-in-Picture window, and
 * `phase` becomes 'pip' so the dialog's own chrome (and the app's nav
 * chrome, hidden while a quick-play session is visible) know to reappear
 * while the video itself stays alive and playing off-screen.
 *
 * ── STATE MACHINE (`phase`) ────────────────────────────────────────────
 *   closed -> preparing -> playing -> pip -> closed
 *   preparing: caching (if needed) + resolving a playable URL
 *   playing:   dialog fullscreen, video loaded/playing
 *   pip:       dialog chrome hidden, video alive+playing in the OS PiP window
 * ───────────────────────────────────────────────────────────────────────
 */

import { ref, computed } from "vue";
import { api } from "@/api/config.js";
import { triggerCache } from "@/api/index.js";
import { useMediaPlayer } from "./useMediaPlayer.js";
import { resolveVideoUrl } from "@/services/resolveVideoUrl.js";
import { supportsPiP, requestPiP } from "@/services/pictureInPicture.js";
import { humanizeError } from "@/services/mediaErrorMessage.js";

/** Module-scoped singleton instance -- shared by the trigger (context menu) and the dialog. */
let episodePlayerInstance = null;

// Cache-and-play poll cadence (same 2s/500ms as useMediaPlayer.js's
// playAllFromIndex so the wait feels identical). The timeout is a STALL
// timeout, not a total-time cap: it's reset every time download progress
// advances (see startCachingFlow), so an actively-progressing long download
// (a 2-hour video can legitimately take 10+ minutes to download + upload to
// R2) never times out — only a genuinely stuck one does. A hard 5-minute cap
// was the original cause of "it cached but the player never played": a slow
// download blew past 5 minutes, the poll gave up, and the server finished
// caching after the client had already stopped watching.
const CACHE_POLL_INTERVAL_MS = 2000;
const CACHE_POLL_FIRST_DELAY_MS = 500;
const CACHE_POLL_STALL_TIMEOUT_MS = 10 * 60 * 1000; // give up only after 10 min with NO progress

export function useEpisodePlayer() {
  // Return the cached instance on every call after the first, so all
  // consumers (the context menu that triggers quickPlay, the dialog that
  // renders it) share identical reactive state.
  if (episodePlayerInstance) return episodePlayerInstance;

  // Narrow, deliberate reuse from the playlist player -- see module doc above
  // for why only these four functions (and nothing queue-related) are pulled in.
  const {
    getEpisodePositionWithIntroSkip,
    setEpisodePosition,
    resetEpisodePosition,
    saveToStorage,
  } = useMediaPlayer();

  /** 'closed' | 'preparing' | 'playing' | 'pip' -- see module doc for the state machine. */
  const phase = ref("closed");
  /** The episode currently quick-played, or null when closed. */
  const episode = ref(null);
  /** Resolved playable URL (presigned R2 or direct server URL) for the dialog's <video src>. */
  const resolvedVideoUrl = ref(null);
  /**
   * Caching-overlay progress/status. Deliberately LOCAL to this composable,
   * not useAppMenu.js's shared cachingProgress/cachingStatusMessage -- those
   * are already shared by list-view polling and the playlist player's own
   * preparing overlay, so reusing them here would let an unrelated caching
   * operation elsewhere in the app stomp on this dialog's progress display.
   */
  const cachingProgress = ref(0);
  const cachingStatusMessage = ref("");
  /** Inline error message for the dialog to display, or null. Reset on every quickPlay(). */
  const error = ref(null);

  /** Whether the dialog component should be mounted at all -- true through preparing/playing/pip. */
  const isOpen = computed(() => phase.value !== "closed");
  /** Whether the dialog's own fullscreen chrome (and the app's hidden nav chrome) should show. */
  const isDialogVisible = computed(
    () => phase.value === "preparing" || phase.value === "playing",
  );

  // Plain closure variables, not refs -- mirrors playAllFromIndex's own poll
  // handling exactly; nothing outside this file ever needs to watch them.
  let cachePollInterval = null;
  let cachePollMsSinceProgress = 0; // ms elapsed since download progress last advanced (stall detector)
  let lastCacheProgress = -1;       // highest progress % seen so far (to detect advancement)

  const clearCachePoll = () => {
    if (cachePollInterval) {
      clearInterval(cachePollInterval);
      cachePollInterval = null;
    }
    cachePollMsSinceProgress = 0;
    lastCacheProgress = -1;
  };

  /** Build the /play/:videoId.:ext URL for an episode, matching the server's routing. */
  const buildPlayUrl = (ep) => {
    const ext = ep.episode_type === "audio" ? "mp3" : "mp4";
    return `${import.meta.env.VITE_API_BASE_URL}/play/${ep.video_id}.${ext}`;
  };

  /**
   * Move to the 'playing' phase: prime the resume position using the same
   * write-before-load technique useMediaPlayer.js's own play functions use
   * (write the intro-skip-adjusted position back into storage synchronously,
   * so the dialog's onLoadedMetadata -- which reads the plain, un-adjusted
   * getter -- sees the already-correct value), then resolve a directly
   * playable URL.
   */
  const startPlaying = async (ep) => {
    const savedPosition = getEpisodePositionWithIntroSkip(ep.id);
    setEpisodePosition(ep.id, savedPosition);
    resolvedVideoUrl.value = await resolveVideoUrl(buildPlayUrl(ep));
    phase.value = "playing";
  };

  /**
   * startCachingFlow - Trigger server-side caching (fire-and-forget, same
   * endpoint/shape the playlist player's playAllFromIndex uses) and poll the
   * GENERIC per-episode status endpoint -- not /playlist/status, since this
   * episode may not be in the playlist at all -- until it's ready, then play
   * automatically. Shared by quickPlay's initial "not cached yet" path and by
   * handlePlaybackError's fallback (a video load can fail even when cached_at
   * looked truthy -- see that function's doc).
   */
  const startCachingFlow = (ep) => {
    phase.value = "preparing";
    error.value = null;
    cachingProgress.value = typeof ep.progress === "number" ? ep.progress : 0;
    cachingStatusMessage.value = `Preparing "${ep.title || "episode"}"...`;

    // triggerCache normalizes a give-up response (e.g. this episode already
    // hit maxDownloadFailures) into a checkable result instead of a bare
    // api.get(...) that a plain .catch() never saw (it's a 200 OK with
    // success:false, not a thrown error). Without this, pollOnce's own
    // error surfacing below never fires either — the give-up gate never
    // writes progress_message, so nothing distinguishes it from a normal
    // in-progress download until the full stall timeout. Confirmed live on
    // episode 262742: progress_message was empty despite the gate being
    // active, so this was the ONLY signal available that anything was wrong.
    triggerCache(ep).then((result) => {
      if (!result.ok) {
        clearCachePoll();
        error.value = result.message;
      }
    });

    const pollOnce = async () => {
      try {
        // NOTE: this endpoint returns the row directly ({ id, cached_at,
        // progress, storage, progress_message }), NOT the { success, data }
        // envelope the /podcast|latest|playlist/status endpoints use.
        const { data } = await api.get(`/episode/status/${ep.id}`);
        if (typeof data.progress === "number") {
          cachingProgress.value = data.progress;
          // Advancement resets the stall clock, so a slow-but-progressing
          // download never hits the timeout no matter how long it takes.
          if (data.progress > lastCacheProgress) {
            lastCacheProgress = data.progress;
            cachePollMsSinceProgress = 0;
          }
        }
        const nowCached = data.cached_at && data.cached_at !== "downloading";
        if (nowCached) {
          clearCachePoll();
          await startPlaying({ ...episode.value, ...data });
          return;
        }
        // Distinguish a normal in-progress phase (nice label, keep polling)
        // from a genuine terminal error (premiere, private video, age-
        // restricted, etc.) -- same distinction MediaPlayer.vue's own
        // onVideoError makes. Without this, a real error would just sit
        // there relabeled as a "status" until the 5-minute timeout, instead
        // of surfacing the actual, more useful message right away.
        if (data.progress_message === "downloading") {
          cachingStatusMessage.value = "Downloading from source…";
        } else if (data.progress_message?.startsWith("Uploading")) {
          cachingStatusMessage.value = "Uploading to cloud…";
        } else if (data.progress_message) {
          clearCachePoll();
          error.value = humanizeError(data.progress_message);
        }
      } catch (err) {
        console.error("Quick play status poll error:", err);
      }
    };

    clearCachePoll();
    cachePollInterval = setInterval(() => {
      // Stall detector: this counter is reset to 0 whenever progress advances
      // (see pollOnce), so it only accumulates while the download is stuck.
      cachePollMsSinceProgress += CACHE_POLL_INTERVAL_MS;
      if (cachePollMsSinceProgress >= CACHE_POLL_STALL_TIMEOUT_MS) {
        clearCachePoll();
        // Non-dead-end message: the server keeps caching in the background, so
        // tapping Play again shortly will find it ready and play immediately.
        error.value = `Still preparing "${ep.title || "episode"}" — it's taking a while. It will keep downloading in the background; try Play again in a moment.`;
      } else {
        pollOnce();
      }
    }, CACHE_POLL_INTERVAL_MS);
    // Also try immediately after a short delay (cache might already be fast/done).
    setTimeout(pollOnce, CACHE_POLL_FIRST_DELAY_MS);
  };

  /**
   * quickPlay - Open the dialog for a single episode and start playback,
   * transparently caching first if needed. Never touches the playlist queue.
   */
  const quickPlay = async (ep) => {
    // If a previous quick-play session is still active (playing, or already
    // handed off to PiP), end it cleanly first -- otherwise this same
    // singleton dialog would just swap the <video> src out from under an
    // active PiP session instead of starting a fresh one.
    if (phase.value !== "closed") {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
      teardown({});
    }

    error.value = null;
    episode.value = ep;
    resolvedVideoUrl.value = null;
    phase.value = "preparing";
    cachingProgress.value = 0;
    cachingStatusMessage.value = `Preparing "${ep.title || "episode"}"...`;

    // Only TRULY cached episodes (cached_at set, not "downloading") play
    // immediately -- mirrors playAllFromIndex's own isCached check exactly.
    // NOTE: this can still turn out to be stale (see handlePlaybackError) --
    // e.g. the cached file was cleaned up after this episode's list data was
    // last fetched -- in which case the video's own onerror recovers.
    const isCached = ep.cached_at && ep.cached_at !== "downloading";
    if (isCached) {
      await startPlaying(ep);
      return;
    }

    startCachingFlow(ep);
  };

  /**
   * handlePlaybackError - called when the dialog's <video> element fails to
   * load/play. quickPlay's cached_at check can be WRONG even when it looked
   * truthy: the file may have been cleaned up (cache retention) or a previous
   * cache attempt may not have actually finished, in ways the episode list's
   * last-fetched data doesn't reflect. Re-check the server's LIVE status
   * (same "is it actually still downloading" logic MediaPlayer.vue's own
   * onVideoError uses) before giving up -- if it's genuinely still
   * downloading, fall back into the same caching/poll flow (auto-retries
   * once ready) instead of a dead-end error.
   */
  const handlePlaybackError = async () => {
    if (!episode.value) return;
    try {
      const { data } = await api.get(`/episode/status/${episode.value.id}`);
      const stillDownloading =
        !data.cached_at &&
        ((data.progress != null && data.progress > 0) ||
          data.progress_message === "downloading" ||
          data.progress_message?.startsWith("Uploading"));
      if (stillDownloading) {
        startCachingFlow({ ...episode.value, ...data });
        return;
      }
      if (data.progress_message && data.progress_message !== "downloading") {
        error.value = humanizeError(data.progress_message);
        return;
      }
    } catch (err) {
      console.error("Quick play error-status check failed:", err);
    }
    error.value = "This episode could not be played. It may need to be re-cached.";
  };

  /**
   * teardown - Fully end the quick-play session: flush the resume position
   * (or clear it, if the episode played all the way to the end), stop any
   * live cache poll, and close the dialog. Pauses the video element first if
   * given one (defensive -- the browser has usually already paused by the
   * time this runs, e.g. after `ended` or `leavepictureinpicture`).
   */
  const teardown = ({ videoEl, currentTime, natural = false } = {}) => {
    if (videoEl && !videoEl.paused) videoEl.pause();
    clearCachePoll();
    if (episode.value) {
      if (natural) {
        resetEpisodePosition(episode.value.id);
      } else if (typeof currentTime === "number") {
        setEpisodePosition(episode.value.id, currentTime);
      }
      saveToStorage();
    }
    phase.value = "closed";
    episode.value = null;
    resolvedVideoUrl.value = null;
  };

  /**
   * close - The dialog's Close (X) button. While actually playing (not
   * mid-caching), on a non-audio episode, with the browser supporting it,
   * AND with the video's metadata actually loaded (readyState check --
   * requestPictureInPicture() throws InvalidStateError otherwise, e.g. if
   * the user taps Close in the brief window before playback has started):
   * hand off to Picture-in-Picture instead of stopping -- the <video>
   * element must stay mounted for this (EpisodePlayerDialog.vue keeps it
   * alive via v-show tied to isOpen, not isDialogVisible). Every other case
   * (audio-only, unsupported, not yet loaded, still preparing, or the PiP
   * request itself rejects) falls back to a full teardown, same as closing
   * always did before this feature existed.
   */
  const close = ({ videoEl, currentTime } = {}) => {
    const canTryPiP =
      phase.value === "playing" &&
      !error.value &&
      episode.value?.episode_type !== "audio" &&
      videoEl?.readyState >= 1 && // HAVE_METADATA -- required by requestPictureInPicture()
      supportsPiP(videoEl);

    if (!canTryPiP) {
      teardown({ videoEl, currentTime });
      return;
    }

    requestPiP(videoEl)
      .then(() => {
        phase.value = "pip";
      })
      .catch((err) => {
        console.error("PiP request failed, closing instead:", err);
        teardown({ videoEl, currentTime });
      });
  };

  episodePlayerInstance = {
    phase,
    episode,
    resolvedVideoUrl,
    cachingProgress,
    cachingStatusMessage,
    error,
    isOpen,
    isDialogVisible,
    quickPlay,
    close,
    teardown,
    handlePlaybackError,
  };
  return episodePlayerInstance;
}
